/**
 * <ingredient-tabs> + <ingredient-picker> — the ingredient/nutrition tab
 * panel (D-19.4). Live bundles this logic (tab switching, drag-to-scroll
 * pill nav, per-ingredient table picker) inside its compiled custom.js;
 * the rebuild ships it as a deferred scoped file (§5: no import graph).
 *
 * Ported behavior:
 *  - pill click: .is-active + aria-selected/tabindex on the pills,
 *    .is-active/.is-hidden on the [data-ingredient-tab-panel] panels,
 *    then the nav scroll-centres the active pill;
 *  - pointer drag scrolls the pill nav (5px threshold, click suppressed
 *    after a drag, .is-dragging while active);
 *  - <ingredient-picker>: dropdown over the combo-ingredient tables —
 *    selecting an entry swaps the visible [data-ingredients-table] figure
 *    inside the same panel and updates the .selected-ingredient label;
 *    outside click closes.
 *
 *  - flavour dropdown in the panels: picking a flavour fetches that
 *    product's ?view=ingredients-panel on demand and swaps every panel's
 *    [data-tab-content] blocks (live prefetched all flavours into
 *    [data-hidden-pool] instead; that pool stays unused).
 */
/*
 * Bundle component panels (snippets/bundle-ingredient-tabs.liquid): each
 * component's tab body sits in a closed <details> and enters the page the
 * first time that <details> opens (the <ingredient-tabs> element then
 * upgrades as usual). Two sources:
 *  - [data-ingredients-src] (storefront): the bundle's
 *    ?view=ingredients-panel (templates/product.ingredients-panel.liquid)
 *    returns every component's body, each in a [data-ingredients-component]
 *    wrapper, so bundle PDPs no longer render them server-side. It is
 *    fetched once per URL, as soon as an accordion holding shells opens
 *    (the section's), so a component usually opens without a wait; each
 *    shell then takes a copy of its component's body;
 *  - <template data-deferred-ingredient-tabs> (theme editor): rendered
 *    inline and stamped.
 * `toggle` does not bubble, so it is caught in the capture phase.
 */
if (!window.__deferredIngredientTabs) {
  window.__deferredIngredientTabs = true;

  // Only this <details>' own panels: the bundle accordions sit inside an
  // outer section accordion, and opening that must not fill them all.
  const ownPanels = (details, selector) =>
    Array.from(details.querySelectorAll(selector)).filter((element) => element.closest('details') === details);

  const stampDeferredIngredientTabs = (details) => {
    ownPanels(details, 'template[data-deferred-ingredient-tabs]').forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  };

  const SPINNER =
    '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';

  // Loading: Dawn's spinner plus a screen-reader label. Error: the label as
  // text, or an empty panel when there is none.
  const showStatus = (host, label, loading) => {
    if (!loading && !label) {
      host.replaceChildren();
      return;
    }
    const status = document.createElement('p');
    status.className = 'ingredient-tabs__status';
    status.setAttribute('role', 'status');
    if (loading) {
      const spinner = document.createElement('span');
      spinner.className = 'loading__spinner';
      spinner.style.position = 'static';
      spinner.innerHTML = SPINNER;
      const text = document.createElement('span');
      text.className = 'visually-hidden';
      text.textContent = label || '';
      status.append(spinner, text);
    } else {
      status.textContent = label;
    }
    host.replaceChildren(status);
  };

  // The view renders without a section, so every section.id slot in the
  // body's ids is empty ("ingredient-tabbtn--2", "Details-section-"). Fill
  // it with the shell's scope, unique per component; any other id gets the
  // scope appended.
  const scopeId = (id, scope) => {
    if (/--\d+$/.test(id)) return id.replace(/--(\d+)$/, `-${scope}-$1`);
    if (id.endsWith('-')) return `${id}${scope}`;
    return `${id}-${scope}`;
  };
  const ID_REFERENCES = ['aria-controls', 'aria-labelledby', 'aria-describedby', 'for'];

  // Give the fetched body what the inline render took from the section:
  // its padding class, its title (heading class, tab-list label), its id in
  // the hidden pool, section-scoped ids, and the section setting that hides
  // the variant dropdown in the named tabs.
  const adoptIngredientsPanel = (host, body) => {
    const { sectionId = '', sectionTitle = '', idScope = '', hideVariantDropdownTabs = '' } = host.dataset;

    body.querySelectorAll('.section--padding').forEach((element) => {
      element.classList.replace('section--padding', `section-${sectionId}-padding`);
      if (sectionTitle) element.classList.remove('no-heading');
    });
    if (sectionTitle) {
      body.querySelectorAll('.ingredient-tabs__nav[role="tablist"]').forEach((nav) => {
        nav.setAttribute('aria-label', sectionTitle);
      });
    }
    body.querySelectorAll('[data-hidden-pool]').forEach((pool) => pool.setAttribute('data-section-id', sectionId));

    const ids = new Set();
    body.querySelectorAll('[id]').forEach((element) => {
      ids.add(element.id);
      element.id = scopeId(element.id, idScope);
    });
    ID_REFERENCES.forEach((attribute) => {
      body.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const references = element.getAttribute(attribute).split(/\s+/);
        element.setAttribute(attribute, references.map((id) => (ids.has(id) ? scopeId(id, idScope) : id)).join(' '));
      });
    });

    // Same match as snippets/ingredient-tabs-dropdown-row.liquid: comma
    // list, each entry stripped, equal to the tab title.
    const hiddenTabs = hideVariantDropdownTabs
      .split(',')
      .map((title) => title.trim())
      .filter(Boolean);
    if (hiddenTabs.length) {
      body.querySelectorAll('[data-ingredient-tab-panel]').forEach((panel) => {
        if (!hiddenTabs.includes(panel.getAttribute('data-accordion'))) return;
        panel.querySelectorAll('.ingredient-tabs-dropdown-row > variant-dropdown').forEach((dropdown) => {
          dropdown.remove();
        });
      });
    }

    host.replaceChildren(body);
  };

  const isStrayText = (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '';

  // The view's top level: the noindex <meta>, whitespace, comments and one
  // wrapper per component. Other elements (a proxy's) are skipped; any text
  // (a "Liquid error:" line) rejects the response. A theme without the view
  // answers with the whole product page, which has no top-level wrapper.
  const readComponentPanels = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const panels = new Map();
    for (const node of template.content.childNodes) {
      if (isStrayText(node)) throw new Error('ingredients panel: stray text');
      if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-ingredients-component')) {
        panels.set(node.getAttribute('data-ingredients-component'), node);
      }
    }
    if (!panels.size) throw new Error('ingredients panel: no component panels');
    return panels;
  };

  // One request per URL for the whole page, kept once it succeeds; a failed
  // one is dropped so the next open retries.
  const panelRequests = new Map();
  const requestPanels = (src) => {
    let request = panelRequests.get(src);
    if (!request) {
      request = { panels: null };
      request.promise = fetch(src)
        .then((response) => {
          if (!response.ok) throw new Error(`ingredients panel: HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          request.panels = readComponentPanels(html);
          return request.panels;
        })
        .catch((error) => {
          if (panelRequests.get(src) === request) panelRequests.delete(src);
          throw error;
        });
      panelRequests.set(src, request);
    }
    return request;
  };

  // A copy of one component's body, so several shells (section and block,
  // or a product listed twice) can show the same component.
  const componentBody = (panels, componentId) => {
    const wrapper = panels.get(componentId);
    if (!wrapper) throw new Error('ingredients panel: component missing');
    const body = wrapper.ownerDocument.createDocumentFragment();
    for (const node of wrapper.childNodes) {
      if (isStrayText(node)) throw new Error('ingredients panel: stray text');
      if (node.nodeType === Node.ELEMENT_NODE) body.append(node.cloneNode(true));
    }
    if (body.childElementCount !== 1 || !body.firstElementChild.matches('[data-product-ingredient-data]')) {
      throw new Error('ingredients panel: unexpected component body');
    }
    return body;
  };

  // Nothing logged; the next open tries again (after a failed request, with
  // a new one — failed requests are dropped).
  const failPanel = (host) => {
    delete host.dataset.ingredientsState;
    showStatus(host, host.dataset.errorLabel, false);
  };

  const fillPanel = (host, panels) => {
    try {
      adoptIngredientsPanel(host, componentBody(panels, host.dataset.ingredientsComponent));
      host.dataset.ingredientsState = 'loaded';
    } catch {
      failPanel(host);
    }
  };

  const loadIngredientsPanels = (details) => {
    details.querySelectorAll('[data-ingredients-src]').forEach((host) => {
      if (host.dataset.ingredientsState) return; // loading or loaded
      const request = requestPanels(host.dataset.ingredientsSrc);
      // A shell in a nested, still closed accordion (the section accordion
      // opened): warm the request only.
      if (host.closest('details') !== details) {
        request.promise.catch(() => {});
        return;
      }
      if (request.panels) {
        fillPanel(host, request.panels);
        return;
      }
      host.dataset.ingredientsState = 'loading';
      showStatus(host, host.dataset.loadingLabel, true);
      request.promise.then(
        (panels) => fillPanel(host, panels),
        () => failPanel(host)
      );
    });
  };

  const openIngredientPanels = (details) => {
    stampDeferredIngredientTabs(details);
    loadIngredientsPanels(details);
  };

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (details instanceof HTMLDetailsElement && details.open) openIngredientPanels(details);
    },
    true
  );
  // A <details> already open at load (e.g. restored state) is handled now.
  document.querySelectorAll('details[open]').forEach(openIngredientPanels);
}

if (!customElements.get('ingredient-tabs')) {
  class IngredientTabs extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.ingredient-tabs__nav');
      this.tabs = Array.from(this.querySelectorAll('.ingredient-tabs__tab'));
      this.panels = Array.from(this.querySelectorAll('[data-ingredient-tab-panel]'));
      if (!this.tabs.length || !this.panels.length) return;

      this.suppressClick = false;
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (event) => {
          if (this.suppressClick) {
            event.preventDefault();
            return;
          }
          this.activate(parseInt(tab.dataset.tabIndex, 10));
        });
      });

      if (this.nav) this.bindDragScroll();

      // The flavour dropdown in the panels: <variant-dropdown> only morphs
      // product cards, so picking a flavour here is handled by the tabs.
      this.addEventListener('click', (event) => {
        const option = event.target.closest('.ingredient-tabs-dropdown-row variant-dropdown .dropdown-variant-wrapper');
        if (option && option.closest('ingredient-tabs') === this) this.switchFlavour(option);
      });
      // Start the request before the click lands: hovering an option
      // (desktop) or touching it (mobile, ~100ms before the click) is enough
      // that the swap is usually instant.
      const prefetch = (event) => {
        const option = event.target.closest('.ingredient-tabs-dropdown-row variant-dropdown .dropdown-variant-wrapper');
        if (option && option.closest('ingredient-tabs') === this) this.requestFlavour(option).catch(() => {});
      };
      this.addEventListener('pointerover', prefetch);
      this.addEventListener('pointerdown', prefetch);
    }

    // One request per flavour URL, kept once it succeeds; a failed one is
    // dropped so the next pick retries.
    requestFlavour(option) {
      const { variantId, productUrl } = option.dataset;
      if (!variantId || !productUrl) return Promise.reject(new Error('ingredients panel: option without product'));
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('view', 'ingredients-panel');

      if (!this.flavourRequests) this.flavourRequests = new Map();
      const requests = this.flavourRequests;
      if (!requests.has(url.href)) {
        requests.set(
          url.href,
          fetch(url.href)
            .then((response) => {
              if (!response.ok) throw new Error(`ingredients panel: HTTP ${response.status}`);
              return response.text();
            })
            .catch((error) => {
              requests.delete(url.href);
              throw error;
            })
        );
      }
      return requests.get(url.href);
    }

    // Swap every panel's contents to the picked flavour: its product's
    // ?view=ingredients-panel renders the same tab body, whose
    // [data-tab-content] blocks replace ours panel by panel (matched on the
    // tab key). The active tab and the ingredient picker's choice stay.
    switchFlavour(option) {
      const { variantId, productUrl } = option.dataset;
      if (!variantId || !productUrl) return;
      const dropdown = option.closest('variant-dropdown');
      const current = dropdown && dropdown.querySelector('.variant-dropdown[data-selected-variant-id]');
      if (current && current.getAttribute('data-selected-variant-id') === variantId) return;

      // The dimmed state only shows when the answer is slow (not prefetched,
      // slow network); a fast swap happens without any loading flash.
      const token = (this.flavourToken = {});
      this.setAttribute('aria-busy', 'true');
      const loadingTimer = setTimeout(() => {
        if (this.flavourToken === token) this.classList.add('is-loading');
      }, 250);
      this.requestFlavour(option)
        .then((html) => {
          if (this.flavourToken !== token) return; // a later pick won
          const template = document.createElement('template');
          template.innerHTML = html;
          const fetchedPanels = new Map();
          template.content.querySelectorAll('[data-ingredient-tab-panel]').forEach((panel) => {
            fetchedPanels.set(panel.getAttribute('data-tab-key'), panel);
          });
          if (!fetchedPanels.size) throw new Error('ingredients panel: no tab panels');

          this.panels.forEach((panel) => this.swapPanelContent(panel, fetchedPanels.get(panel.getAttribute('data-tab-key'))));
          this.markFlavour(option);
        })
        .catch(() => {})
        .finally(() => {
          clearTimeout(loadingTimer);
          if (this.flavourToken !== token) return;
          this.removeAttribute('aria-busy');
          this.classList.remove('is-loading');
        });
    }

    swapPanelContent(panel, fetchedPanel) {
      const host = panel.querySelector('.accordion__content');
      if (!host) return;
      host.querySelectorAll(':scope > [data-tab-content]').forEach((block) => block.remove());
      if (!fetchedPanel) return;
      fetchedPanel.querySelectorAll('.accordion__content > [data-tab-content]').forEach((block) => {
        host.append(document.importNode(block, true));
      });
      // Keep the ingredient picker's choice on the new tables.
      const picked = panel.querySelector('ingredient-picker .selected-ingredient');
      const tables = Array.from(panel.querySelectorAll('[data-ingredients-table]'));
      if (picked && tables.length) {
        const name = picked.textContent.trim();
        const hasMatch = tables.some((table) => table.getAttribute('data-ingredients-table') === name);
        tables.forEach((table, index) => {
          table.classList.toggle('hidden', hasMatch ? table.getAttribute('data-ingredients-table') !== name : index !== 0);
        });
      }
    }

    // Every panel carries its own copy of the dropdown: move the selection,
    // label, swatch and checkmark on all of them.
    markFlavour(option) {
      const { variantId, shortName, productName, color } = option.dataset;
      this.querySelectorAll('.ingredient-tabs-dropdown-row variant-dropdown').forEach((dropdown) => {
        const toggle = dropdown.querySelector('.variant-dropdown');
        if (!toggle) return;
        toggle.setAttribute('data-selected-variant-id', variantId);
        const label = toggle.querySelector('.variant-dropdown-product');
        if (label) {
          if (shortName) label.textContent = shortName;
          if (productName) label.setAttribute('data-selected-variant', productName);
        }
        const swatch = toggle.querySelector('.variant-dropdown-color');
        if (swatch && color) swatch.style.background = color;
        dropdown.querySelectorAll('.dropdown-variant-wrapper').forEach((wrapper) => {
          const wrapperSwatch = wrapper.querySelector('.variant-dropdown-color');
          if (wrapperSwatch) wrapperSwatch.classList.toggle('selected-variant', wrapper.dataset.variantId === variantId);
        });
      });
    }

    activate(index) {
      this.tabs.forEach((tab) => {
        const isActive = parseInt(tab.dataset.tabIndex, 10) === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive && this.nav) {
          const navRect = this.nav.getBoundingClientRect();
          const tabRect = tab.getBoundingClientRect();
          this.nav.scrollTo({
            left: this.nav.scrollLeft + tabRect.left - navRect.left - (navRect.width - tabRect.width) / 2,
            behavior: 'smooth',
          });
        }
      });
      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.classList.toggle('is-active', isActive);
        panel.classList.toggle('is-hidden', !isActive);
      });
    }

    bindDragScroll() {
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;

      this.nav.addEventListener('pointerdown', (event) => {
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = this.nav.scrollLeft;
      });
      this.nav.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 5) {
          moved = true;
          this.nav.classList.add('is-dragging');
          this.nav.scrollLeft = startScroll - delta;
        }
      });
      const stop = () => {
        if (!dragging) return;
        dragging = false;
        this.nav.classList.remove('is-dragging');
        if (moved) {
          this.suppressClick = true;
          setTimeout(() => {
            this.suppressClick = false;
          }, 0);
        }
      };
      this.nav.addEventListener('pointerup', stop);
      this.nav.addEventListener('pointerleave', stop);
      this.nav.addEventListener('pointercancel', stop);
    }
  }

  customElements.define('ingredient-tabs', IngredientTabs);
}

if (!customElements.get('ingredient-picker')) {
  class IngredientPicker extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.variant-dropdown');
      this.selectedLabel = this.querySelector('.selected-ingredient');
      this.options = Array.from(this.querySelectorAll('.dropdown-ingredient-selection .dropdown-variant-wrapper'));
      if (!this.trigger || !this.options.length) return;

      this.trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        if (this.hasAttribute('open')) {
          this.removeAttribute('open');
        } else {
          this.setAttribute('open', '');
        }
      });

      this.options.forEach((option) => {
        option.addEventListener('click', () => this.select(option));
      });

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.removeAttribute('open');
      };
      document.addEventListener('click', this.onDocumentClick, true);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
    }

    select(option) {
      const name = option.textContent.trim();
      if (this.selectedLabel) this.selectedLabel.textContent = name;
      this.options.forEach((entry) => {
        entry.style.display = entry === option ? 'none' : '';
      });
      const panel = this.closest('[data-ingredient-tab-panel]');
      if (panel) {
        panel.querySelectorAll('[data-ingredients-table]').forEach((table) => {
          table.classList.toggle('hidden', table.getAttribute('data-ingredients-table') !== name);
        });
      }
      this.removeAttribute('open');
    }
  }

  customElements.define('ingredient-picker', IngredientPicker);
}
